import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { cmApi } from "./cmApi";
import VersionBadge from "../../../components/compliance/mvp/VersionBadge";

// Live-doc viewer: edit the moving live head, approve to snapshot an immutable
// version. Version history is shown read-only below.
export default function PolicyViewer() {
  const { slug } = useParams();
  const [policy, setPolicy] = useState(null);
  const [versions, setVersions] = useState([]);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const data = await cmApi.getPolicy(slug);
    setPolicy(data.policy);
    setVersions(data.versions || []);
    setDraft(data.policy.live_head_md || "");
  }, [slug]);

  useEffect(() => {
    load().catch(() => setStatus("Could not load policy."));
  }, [load]);

  const saveHead = async () => {
    await cmApi.editLiveHead(slug, draft);
    setStatus("Live head saved.");
    load();
  };

  const approve = async () => {
    await cmApi.approvePolicy(slug, "current-user", "Approved from MVP UI");
    setStatus("New version approved.");
    load();
  };

  if (!policy) {
    return (
      <div className="p-6" data-testid="cm-policy-viewer">
        <p className="text-sm text-slate-500">{status || "Loading…"}</p>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="cm-policy-viewer">
      <Link to="/compliance/mvp/policies" className="text-sm text-indigo-600">
        ← Policy Library
      </Link>
      <h1 className="mt-2 text-xl font-semibold text-slate-800">{policy.title}</h1>
      <p className="text-xs text-slate-500">{policy.slug}</p>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-slate-700">Live head (editable)</h2>
        <textarea
          className="mt-2 h-48 w-full rounded border border-slate-300 p-3 font-mono text-sm"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="cm-live-head"
        />
        <div className="mt-2 flex gap-2">
          <button
            onClick={saveHead}
            className="rounded bg-slate-700 px-3 py-1 text-sm text-white"
            data-testid="cm-save-head"
          >
            Save live head
          </button>
          <button
            onClick={approve}
            className="rounded bg-emerald-600 px-3 py-1 text-sm text-white"
            data-testid="cm-approve"
          >
            Approve new version
          </button>
        </div>
        {status && <p className="mt-2 text-sm text-slate-500">{status}</p>}
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700">Version history</h2>
        {versions.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No approved versions yet.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {versions.map((v) => (
              <li
                key={v._id}
                className="flex items-center gap-3 rounded border border-slate-200 px-3 py-2"
              >
                <VersionBadge version={v.version} status={v.status} />
                <span className="text-xs text-slate-500">
                  {v.approved_at} · {v.approved_by}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
